import axios from 'axios';


let isFetchingToken = false;
let tokenPromise = null;

export const getUwinClientToken = async () => {
	const savedToken = localStorage.getItem("uwin_token");
	const tokenTimestamp = localStorage.getItem("uwin_token_timestamp");
	const NINE_MINUTES = 9 * 60 * 1000;

	if (savedToken && tokenTimestamp && (Date.now() - parseInt(tokenTimestamp)) < NINE_MINUTES) {
		return savedToken;
	}

	if (isFetchingToken) {
		return tokenPromise;
	}

	isFetchingToken = true;
	tokenPromise = (async () => {
		try {
			const userDetails = JSON.parse(
				localStorage.getItem("uwin_loggedinUserdetails")
			);

			const response = await axios.post(
				"/nodeapi/uwin/client-token",
				{
					user_id: userDetails?.user_id ?? null,
				}
			);

			if (response?.data?.success) {
				const token = response?.data?.data?.accessToken;
				localStorage.setItem("uwin_token", token);
				localStorage.setItem("uwin_token_timestamp", Date.now().toString());
				return token;
			}

			throw new Error("Failed to get UWIN client token");
		} finally {
			isFetchingToken = false;
		}
	})();

	return tokenPromise;
};
const uwinClient = axios.create({
	baseURL: 'nodeapi',
	timeout: 30000,
});

// Automatically refresh and attach token to every request
uwinClient.interceptors.request.use(
	async (config) => {
		let token = null
		// This will fetch a new token if expired, or return the cached one instantly
		if (localStorage.getItem("uwin_loggedinUserdetails") == null) {
			token = localStorage.getItem("uwin_token");
		} else {
			token = await getUwinClientToken();
		}
		if (token) {
			config.headers["accessToken"] = token;
		}
		
		const username = JSON.parse(localStorage.getItem("uwin_username"));

		Object.assign(config.headers, { username });
		return config;
	},
	(error) => Promise.reject(error)
);


uwinClient.interceptors.response.use(
	(response) => response,
	(error) => {
		const status = error.response?.status;

		if (status === 401) {
			return {
				data: null,
				status: 401,
				statusText: "Unauthorized",
				headers: {},
				config: error.config,
			};
		}

		return Promise.reject(error);
	}
);


export const uwinAPI = {
	decryptText: async (data) => (await uwinClient.post(`/uwin/decryption`, data)).data,
	encryptText: async (data) => (await uwinClient.post(`/uwin/encryption`, data)).data,
	multipleEncryption: async (data) => (await uwinClient.post(`/uwin/multiple-encryption`, data)).data,
	multipleDecryption: async (data) => (await uwinClient.post(`/uwin/multiple-decryption`, data)).data,
	getUserDetails: async () => {
		const refreshToken = localStorage.getItem('uwin_refresh_token');
		return (await uwinClient.post('/uwin/user-details', { refreshToken })).data;
	},
	getFacilitySessionSites: async () => (await uwinClient.get('/uwin/get-facility-session-sites')).data,
	getBenCategories: async (data) => (await uwinClient.post(`/uwin/beneficiary-type-by-dob`, data)).data,
	getPhotoIdTypes: async (data) => (await uwinClient.post(`/uwin/photo-ids-by-dob`, data)).data,
	preRegister: async (data) => (await uwinClient.post('/uwin/pre-register', data)).data,
	getVaccinations: async (data) => (await uwinClient.post(`/uwin/vaccinations-chart`, data)).data,
	updateVaccination: async (id, data) => (await uwinClient.put(`/uwin/vaccinations/${id}`, data)).data,
	getLinkedChildren: async (motherId) => (await uwinClient.get(`/uwin/linked-children/${motherId}`)).data,
	searchBeneficiary: async (data) => (await uwinClient.post('/uwin/search-beneficiaries', data)).data,
	getBeneficiaryProfile: async (data) => (await uwinClient.post('/uwin/beneficiary-details', data)).data,
	getPregnancyDetails: async (data) => (await uwinClient.post('/uwin/pregnancy-details', data)).data,
	registerPatient: async (data) => (await uwinClient.post('/uwin/uwin-registration', data)).data,
	getSessionBySite: async (data) => (await uwinClient.post('/uwin/session-by-site', data)).data,
	sessionBeneficiaryData: async (data) => (await uwinClient.post('/uwin/session-beneficiary-data', data)).data,
	getPhotoIdsByDob: async (data) => (await uwinClient.post('/uwin/photo-ids-by-dob', data)).data,
	saveHemoglobinDetail: async (data) => (await uwinClient.post('/uwin/save-hemoglobin-detail', data)).data,
	// sendOTP: async(data) =>  (await uwinClient.post('/uwin/send-mobile-update-otp',  data)).data,
	// verifyOTP: async(data) =>  (await uwinClient.post('/uwin/verify-mobile-update-otp',  data)).data,
	verifyBeneficiary: async (data) => (await uwinClient.post('/uwin/verify-beneficiary', data)).data,
	getVillages: async (data) => (await uwinClient.post('/uwin/get-villages-by-site-id', data)).data,
	allUipMaterials: async (data) => (await uwinClient.get('/uwin/all-uip-materials', data)).data,
	eligibleMaterialsCurrentVaccinations: async (data) => (await uwinClient.post('/uwin/eligible-materials-current-vaccinations', data)).data,
	eligibleMaterialsPrevVaccinations: async (data) => (await uwinClient.post('/uwin/eligible-materials-prev-vaccinations', data)).data,
	generateBenOTP: async (data) => (await uwinClient.post('/uwin/generate-ben-otp', data)).data,
	userVerifyOTP: async (data) => (await uwinClient.post('/uwin/verify-otp', data)).data,
	ashaVaccinationTagging: async (data) => (await uwinClient.post('/uwin/asha-vaccination-tagging', data)).data,
	createVaccinationRecord: async (data) => (await uwinClient.post('/uwin/vaccination', data)).data,
	addBenTooGoingSession: async (data) => (await uwinClient.post('/uwin/add-ben-toongoing-session', data)).data,
	sessionSummary: async (data) => (await uwinClient.post('/uwin/session-summary', data)).data,
	getSessionDetails: async (data) => (await uwinClient.post('/uwin/get-session-details', data)).data,

	minDateForDeathAndAbortion: async (data) => (await uwinClient.post('/uwin/get-min-date-for-declare-death-abortion', data)).data,
	declarePregnancy: async (data) => (await uwinClient.post('/uwin/add-pregnancy', data)).data,
	getPregnancyMaster: async () => (await uwinClient.get('/uwin/pregnancy-masters')).data,
	savePregnancyOutcome: async (data) => (await uwinClient.post('/uwin/pregnancy-outcome', data)).data,
	vaccinationNotDoneReasons: async (data) => (await uwinClient.get('uwin/vaccination-not-done-reasons', data)).data,
	updateBeneficiaryVerify: async (data) => (await uwinClient.post('uwin/update-beneficiary-verify', data)).data,
	downloadMcpCardAcknowledgement: async (data) => (await uwinClient.post('uwin/mcp-card', data, { responseType: 'blob' })).data,
	startSession: async (data) => (await uwinClient.post('uwin/start-session', data)).data,
	endSession: async (data) => (await uwinClient.post('uwin/end-session', data)).data,
	undoVaccination: async (data) => (await uwinClient.post('uwin/undo-vaccination', data)).data,
	getCCPSitesForHFM: async () => (await uwinClient.get('uwin/get-ccp-sites-for-hfm')).data,
	getUIPSessionType: async () => (await uwinClient.get('uwin/get-uip-session-type')).data,
	sessionCreation: async (data) => (await uwinClient.post('uwin/session-creation', data)).data,
	downloadFile: async (data) => (await uwinClient.post('/uwin/download-file', data, { responseType: 'blob' })).data,

	getPregnancyDetailsByPrgId: async (data) => (await uwinClient.post('/uwin/pregnancy-details-by-number', data)).data,
	addChildAgainstPregnancy: async (data) => (await uwinClient.post('/uwin/add-child-against-pregnancy', data)).data,

	// asha due list
	getAllAshaSessionSites: async () => (await uwinClient.get('/uwin/get-all-sites-of-asha')).data,
	ashaDueLists: async (data) => (await uwinClient.post('/uwin/due-list-asha', data)).data,
	ashaDueListsDownload: async (data) => (await uwinClient.post('/uwin/download-due-list-asha', data, { responseType: 'blob' })).data,
	sessionSummary: async (data) => (await uwinClient.post('/uwin/session-summary', data)).data,
};